import '@justgiving/loading/src/Loading.scss';

import React, { FunctionComponent } from 'react';

import { NetworkStatus, gql } from '@apollo/client';
import { sha256 } from 'js-sha256';

import { Donation, EsSupportersListQuery } from '../../../types/__generated__';
import useFullPageQuery from '../../hooks/useFullPageQuery';
import DonateButton from '../DonateButton/DonateButton';
import PageSection from '../PageSection/PageSection';
import ESSupportersListSkeleton from './ESSupportersListSkeleton';
import {
  PageComponentsProvider,
  SupportersList,
  SupporterDetailsPlaceholder,
} from '@justgiving/page-components';

import styles from './ESSupportersList.scss';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { SIZE_136_136 } from '../../constants/imageTemplateStrings';
import { makePhotoUrl } from '../../utils/makePhotoUrl';
import { useTranslation } from 'react-i18next';

export const DOCUMENT = gql`
  query ESSupportersList(
    $after: String
    $type: PageType!
    $slug: Slug
    $preview: Preview
    $id: ID
    $donationValue: SortBy
  ) {
    page(type: $type, slug: $slug, id: $id, preview: $preview) {
      id
      legacyId

      supporters(
        first: 5
        after: $after
        statuses: [Succeeded]
        sort: { donationValue: $donationValue }
      ) {
        edges {
          node {
            donation {
              id
              legacyId
              creationDate
              amount {
                value
                currencyCode
              }
              giftAidAmount {
                value
                currencyCode
              }
              displayName
              avatar
              message
            }
          }
        }
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

type ESSupportersListProps = {
  noSupportersMessage: string;
  displayDonateButton?: boolean;
  donateButtonExperiments?: string[];
};

const ESSupportersList: FunctionComponent<ESSupportersListProps> = ({
  noSupportersMessage,
  displayDonateButton,
  donateButtonExperiments,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { error, data, fetchMore, refetch, networkStatus } = useFullPageQuery<
    EsSupportersListQuery
  >(DOCUMENT, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      donationValue: undefined,
    },
  });

  const { data: basePageData } = useBasePageQuery();

  if (
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.refetch ||
    networkStatus === NetworkStatus.setVariables
  ) {
    return <ESSupportersListSkeleton animated displayRefreshButton={false} />;
  }

  if (error) {
    return <ESSupportersListSkeleton onRefresh={refetch} />;
  }

  if (!data?.page?.supporters || !basePageData?.page?.donationSummary) {
    return null;
  }

  const {
    page: {
      legacyId,
      supporters: {
        edges,
        totalCount,
        pageInfo: { hasNextPage, endCursor },
      },
    },
  } = data;

  async function handleFetchMore() {
    await fetchMore({
      variables: { after: endCursor },
    });
  }

  // TODO: Hack to suppress all donor generated content for a page when necessary.
  // This approach obfuscates the slugs of the affected pages in source code.
  // Comments are stripped from compiled code, so commenting the slug is safe - and useful!
  const suppressDonorGeneratedContent = [
    'd048b26b1960d805c8317584ee704e466014cda0055d6b6832286362ac7c1d48', // Matt-Hancock-London-Marathon
    'd3cfe8cc2c96f00681107a27f9accfa58609a902f31997a658041f50d73a3f65', // Matt-Hancock-Climbs-Mont-Blanc
    '312749ef6913f8ccba9a3881cee79a8321c95852688c8496e955811445f7668e', // Gina-Coladangelo1
    'ddb12ef3091955a80908bd56fc4472383ab97cb2d322c670dcd59d09d3c36282', // Polly-Portwin3
  ].includes(sha256.hex(legacyId));

  const supporters = edges.map(supporter => {
    const supporterDetails = supporter.node?.donation as Donation;

    const donationAmount = supporterDetails.amount?.value / 100 || undefined;
    const giftAidAmount =
      supporterDetails.giftAidAmount?.value / 100 || undefined;
    const avatarUrl = suppressDonorGeneratedContent
      ? ''
      : makePhotoUrl(supporterDetails.avatar, SIZE_136_136);
    const userName = suppressDonorGeneratedContent
      ? ''
      : supporterDetails.displayName;
    const message = suppressDonorGeneratedContent
      ? ''
      : supporterDetails.message;

    return {
      date: supporterDetails.creationDate,
      donationAmount: donationAmount,
      currencyCode: supporterDetails.amount?.currencyCode,
      giftAidAmount: giftAidAmount,
      image: avatarUrl,
      userName: userName,
      message: message,
    };
  });

  const donateButton = (
    <DonateButton
      className={styles.button}
      inactivePageBehaviour="hide"
      trackingDataPageSection="SupportersList"
      experiments={donateButtonExperiments}
      successType="SupportersDonateButtonClick"
      id="pages-ui-supporters-donate-button"
    />
  );

  return totalCount === 0 ? (
    <PageSection
      data-qa="test-id"
      className={styles.container}
      title={t('supporters.noSupporters', 'No supporters yet')}
      count={totalCount}
    >
      <p data-qa="no-supporters">{noSupportersMessage}</p>
      <div>
        <PageComponentsProvider>
          <SupporterDetailsPlaceholder />
        </PageComponentsProvider>
      </div>
      {displayDonateButton && donateButton}
    </PageSection>
  ) : (
    <PageSection className={styles.container}>
      <PageComponentsProvider>
        <SupportersList
          totalCount={totalCount}
          supporters={supporters}
          hasNextPage={hasNextPage}
          onClick={handleFetchMore}
          donateButton={displayDonateButton && donateButton}
        />
      </PageComponentsProvider>
    </PageSection>
  );
};

export default ESSupportersList;
